import React, { useMemo } from "react";
import { Plus, Trash2, AlertTriangle } from "lucide-react";
import { useProject } from "../../state/ProjectContext";
import { Pill } from "../ui-prim";
import { AIPanel } from "../AIPanel";
import { prompts } from "../../lib/ai";
import { pct, fK, statusClass, todayISO, toast } from "../../lib/utils";
import Gantt from "../Gantt";

// Build a schedule-item→open-risk index — mirrors backend
// risk_schedule_links.compute_schedule_risk_index().
function useRiskIndex(raids) {
  return useMemo(() => {
    const RANK = { Critical: 4, High: 3, Medium: 2, Low: 1 };
    const COLOR = { Critical: "#DC2626", High: "#F97316", Medium: "#F59E0B", Low: "#22C55E" };
    const idx = {};
    for (const r of (raids || [])) {
      const rtype = (r.type || "").toLowerCase();
      const rstat = (r.status || "").toLowerCase();
      if (!["risk", "issue"].includes(rtype)) continue;
      if (["closed", "resolved"].includes(rstat)) continue;
      const sev = r.score || r.impact || "Medium";
      for (const sid of (r.linkedScheduleIds || [])) {
        if (!sid) continue;
        const e = idx[sid] || { count: 0, maxRank: 0, maxSeverity: "", color: COLOR.Medium };
        e.count += 1;
        const rank = RANK[sev] || 0;
        if (rank > e.maxRank) { e.maxRank = rank; e.maxSeverity = sev; e.color = COLOR[sev] || COLOR.Medium; }
        idx[sid] = e;
      }
    }
    return idx;
  }, [raids]);
}

export default function Dashboard() {
  const { doc, mutate, readOnly } = useProject();
  const riskIndex = useRiskIndex(doc.raids);
  const ta = doc.budget.reduce((s, b) => s + b.allocated, 0);
  const ts = doc.budget.reduce((s, b) => s + b.actual, 0);
  const oR = doc.raids.filter(r => r.type === "Risk" && r.status !== "Closed").length;
  const oI = doc.raids.filter(r => r.type === "Issue" && r.status !== "Closed").length;
  const oA = doc.actions.filter(a => a.status === "Overdue").length;
  const cR = doc.requirements.filter(r => r.status === "Completed").length;

  const updateMilestone = (i, key, value) => {
    mutate(d => ({ ...d, milestones: d.milestones.map((m, idx) => idx === i ? { ...m, [key]: value } : m) }));
  };
  const removeMilestone = (i) => {
    if (!window.confirm("Delete this milestone?")) return;
    mutate(d => ({ ...d, milestones: d.milestones.filter((_, idx) => idx !== i) }));
  };
  const addMilestone = () => {
    mutate(d => ({ ...d, milestones: [...d.milestones, { name: "New milestone", date: todayISO(), status: "Upcoming" }] }));
    toast("Milestone added — edit it inline");
  };

  const MS_STATUSES = ["Completed", "In Progress", "Upcoming", "At Risk", "Delayed"];

  const radar = [
    { l: "Schedule",     s: "On Track" },
    { l: "Budget",       s: "On Track" },
    { l: "Scope",        s: "At Risk" },
    { l: "Quality",      s: "On Track" },
    { l: "Resources",    s: "At Risk" },
    { l: "Compliance",   s: "Escalated" },
    { l: "Vendor",       s: "On Track" },
    { l: "Stakeholders", s: "On Track" },
  ];
  const radarDot = { "On Track": "#22C55E", "At Risk": "#F59E0B", "Escalated": "#EF4444" };

  return (
    <>
      <div className="flex items-end justify-between mb-6 flex-wrap gap-3">
        <div>
          <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-zinc-500 section-eyebrow">Overview</div>
          <h2 className="font-display text-2xl md:text-3xl font-extrabold tracking-tight text-zinc-900 mt-1">Executive Dashboard</h2>
        </div>
        <div className="flex items-center gap-2">
          <Pill tone={statusClass(doc.meta.health).replace("pill-", "")}>{doc.meta.health}</Pill>
        </div>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6" data-export-capture="dashboard-kpis" data-export-title="Dashboard · Executive KPIs">
        <KPI label="Budget Spent" valueClass="text-green-700" value={`${pct(ts, ta)}%`} sub={`${fK(ts)} / ${fK(ta)}`} progress={pct(ts, ta)} pColor="#22C55E"/>
        <KPI label="Open Risks" valueClass="text-amber-700" value={oR} sub={`${doc.raids.filter(r=>r.score==='Critical').length} critical severity`}/>
        <KPI label="Open Issues" valueClass="text-red-700" value={oI} sub={`${oA} overdue actions`}/>
        <KPI label="Requirements" valueClass="text-blue-700" value={`${cR}/${doc.requirements.length}`} sub={`${pct(cR, doc.requirements.length)}% complete`}
             progress={pct(cR, doc.requirements.length)} pColor="#3B82F6" />
      </div>

      <div data-export-capture="dashboard-gantt" data-export-title="Dashboard · Timeline & Critical Path">
        <Gantt tasks={doc.tasks} start={doc.meta.start} end={doc.meta.end} methodology={doc.meta.methodology || "Waterfall"} />
      </div>

      {/* Health Radar + Milestones */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mt-4">
        <div className="card p-5" data-export-capture="dashboard-health-radar" data-export-title="Dashboard · Project Health Radar">
          <div className="font-mono text-[10px] uppercase tracking-[0.14em] text-zinc-500 mb-3">Project Health Radar</div>
          {radar.map(h => (
            <div key={h.l} className="flex items-center gap-3 py-2 border-b border-[var(--border)] last:border-0">
              <div className="w-2.5 h-2.5 rounded-full" style={{ background: radarDot[h.s] || "#22C55E" }}/>
              <div className="flex-1 text-sm text-zinc-800">{h.l}</div>
              <Pill tone={statusClass(h.s).replace("pill-", "")}>{h.s}</Pill>
            </div>
          ))}
        </div>
        <div className="card p-5">
          <div className="flex items-center justify-between mb-3">
            <div className="font-mono text-[10px] uppercase tracking-[0.14em] text-zinc-500">Milestones</div>
            {!readOnly && (
              <button className="btn btn-ghost btn-sm" onClick={addMilestone} data-testid="btn-add-milestone" title="Add milestone">
                <Plus size={11}/>
              </button>
            )}
          </div>
          {doc.milestones.map((m, i) => {
            const key = m.id || m.name;
            const risk = riskIndex[key];
            return (
            <div key={i} className="flex items-center gap-2 p-2 mb-1.5 bg-zinc-50 border border-[var(--border)] rounded-sm group">
              <div className="w-7 h-7 rounded-sm bg-white border border-[var(--border)] grid place-items-center font-mono text-[10px] text-zinc-500 shrink-0">{i+1}</div>
              <div className="flex-1 min-w-0 grid grid-cols-[1fr_120px] gap-2">
                <div className="flex items-center gap-2">
                  <input className="fld text-sm flex-1" defaultValue={m.name} readOnly={readOnly}
                         onBlur={e => e.target.value !== m.name && updateMilestone(i, "name", e.target.value)}
                         data-testid={`milestone-name-${i}`}/>
                  {risk && (
                    <span
                      className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-mono font-semibold text-white shrink-0"
                      style={{ background: risk.color }}
                      title={`${risk.count} open risk(s) linked · max ${risk.maxSeverity}`}
                      data-testid={`milestone-risk-flag-${i}`}
                    >
                      <AlertTriangle size={10} />
                      {risk.count > 1 ? risk.count : ""}
                    </span>
                  )}
                </div>
                <input type="date" className="fld text-xs" defaultValue={m.date} readOnly={readOnly}
                       onBlur={e => updateMilestone(i, "date", e.target.value)}/>
              </div>
              <select className="fld text-xs w-32" value={m.status} disabled={readOnly}
                      onChange={e => updateMilestone(i, "status", e.target.value)}
                      data-testid={`milestone-status-${i}`}>
                {MS_STATUSES.map(s => <option key={s}>{s}</option>)}
              </select>
              {!readOnly && (
                <button className="btn btn-ghost btn-sm opacity-0 group-hover:opacity-100" onClick={() => removeMilestone(i)} title="Delete" data-testid={`btn-del-milestone-${i}`}>
                  <Trash2 size={11}/>
                </button>
              )}
            </div>
            );
          })}
          {doc.milestones.length === 0 && (
            <div className="text-xs text-zinc-500 italic py-4 text-center">No milestones yet. Click <Plus size={11} className="inline -mt-0.5"/> to add one.</div>
          )}
        </div>
      </div>

      <div className="mt-6">
        <AIPanel title="Executive Status Update" section="exec" buildPrompt={() => prompts.execUpdate(doc)} />
      </div>
    </>
  );
}

function KPI({ label, value, sub, progress, pColor, valueClass = "" }) {
  return (
    <div className="card tile-accent p-4">
      <div className="font-mono text-[10px] uppercase tracking-[0.14em] text-zinc-500">{label}</div>
      <div className={`font-display text-3xl font-extrabold tracking-tighter mt-1 ${valueClass}`}>{value}</div>
      {sub && <div className="font-mono text-[10px] text-zinc-500 mt-1">{sub}</div>}
      {progress !== undefined && (
        <div className="h-1 mt-3 bg-zinc-100 rounded-sm overflow-hidden">
          <div className="h-full bar-fill" style={{ width: `${progress}%`, background: pColor || "#FAFAFA" }}/>
        </div>
      )}
    </div>
  );
}
